<template>
    <div class="project-list-widget">

        <div class="card">
            <h3 class="card-header d-flex justify-content-between">
                {{ title }}
                <div class="d-flex justify-content-between">
                    <router-link class="link-light ml-sm-4 mt-2 mt-sm-0"
                                 v-if="canCreate"
                                 variant="primary"
                                 :to="{name:'projectCreateAgency', params: { agencyId: $user.getAgencyId()}}">
                        New Project
                        <b-icon-plus-square/>
                    </router-link>
                </div>
            </h3>
            <div class="card-body">
                <data-table
                    :show-filters="false"
                    :default-items-per-page="10"
                    :show-pager="true"
                    :show-per-page="true"
                    :get-items="getItems"
                    v-model="filters"
                    :fields="fields">
                    <template v-slot:customField="slotProps">
                        <div v-if="slotProps.row.field.key === 'actions'" class="action-buttons d-flex">
                            <b-btn size="sm"
                                   variant="secondary"
                                   @click="viewItem(slotProps.row.item)">
                                <b-icon-eye font-scale="1"/>
                            </b-btn>
                        </div>
                        <div v-else-if="slotProps.row.field.key === 'projectManager'">
                            <div v-for="manager in slotProps.row.item.projectManager" v-bind:key="manager.id">
                                <user-details :user="manager"/>
                            </div>
                        </div>
                        <div v-else-if="slotProps.row.field.key === 'primeCompanies'">
                            <company-link-list :companies="slotProps.row.item.primeCompanies"/>
                        </div>
                        <div v-else-if="slotProps.row.field.key === 'agency'">
                            {{ slotProps.row.item.agency ? slotProps.row.item.agency.name : '' }}
                        </div>
                        <div v-else-if="slotProps.row.field.key === 'audits'">
                            <audit-badge :audit="slotProps.row.item.currentAudit"/>
                        </div>
                        <div v-else-if="slotProps.row.field.key === 'exemptions'">
                            <template v-if="slotProps.row.item.pendingExemptions.length > 0">
                                <router-link :to="`/exemption/list`" class="exemption-count d-block">Requests
                                    <b-badge variant="danger" class="exemption-count__badge ml-2">
                                        {{ slotProps.row.item.pendingExemptions.length }}
                                    </b-badge>
                                </router-link>
                            </template>
                        </div>
                        <div v-else-if="slotProps.row.field.key === 'status'">
                            {{ getStatusLabel(slotProps.row.item.status) }}
                        </div>
                        <div v-else>
                            {{ slotProps.row.item[slotProps.row.field.key] }}
                        </div>
                    </template>
                </data-table>
            </div>
        </div>
    </div>
</template>

<script>
import UserDetails from '@/components/UserDetails'
import Project from '@/models/Project'
import Authorization from '@/providers/Authorization'

export default {
    name: 'ProjectListWidget',
    components: { UserDetails },
    props: {
        title: { type: String, default: 'Projects' },
        showExemptions: { type: Boolean, default: false },
        showAgency: { type: Boolean, default: true },
        filterByAgency: { type: Boolean, default: false },
        filterByProjectManager: { type: Boolean, default: false },
        filterByAuditor: { type: Boolean, default: false },
    },
    data () {
        return {
            canCreate: false,
        }
    },
    computed: {
        fields () {
            const fields = [
                { key: 'name', sortable: true, label: 'Name' },
                { key: 'projectManager', sortable: false, label: 'Project Manager' },
                { key: 'primeCompanies', sortable: false, label: 'Prime Contractors' },
                { key: 'status', sortable: true, label: 'Status' },
                { key: 'audits', sortable: false, label: 'Audits' },
                { key: 'actions', sortable: false, label: 'Actions' },
            ]
            if (this.showExemptions) {
                fields.splice(4, 0, { key: 'exemptions', sortable: false, label: 'Exemptions' })
            }
            if (this.showAgency) {
                fields.splice(2, 0, { key: 'agency', sortable: true, label: 'Agency' })
            }
            return fields
        },
        filters() {
            let filters = {}
            if (this.filterByProjectManager) {
                filters.projectManagerUserId = { field: 'projectManagerUserId', type: 'hidden', value: this.$user.userId() }
            }
            if (this.filterByAgency) {
                filters.agency_id = { field: 'agency_id', type: 'hidden', value: this.$user.getAgencyId() }
            }
            if (this.filterByAuditor) {
                filters.auditorUserId = { field: 'auditorUserId', type: 'hidden', value: this.$user.userId() }
            }
            return filters
        }
    },
    methods: {
        getItems (listParams) {
            const expands = ['agency']
            return this.$api.getItems(this, 'project', this.filters, listParams, expands)
        },
        viewItem (item) {
            this.$router.push(`/project/view/${item.id}`)
        },
        getStatusLabel (value) {
            return Project.getStatusLabel(value)
        },
    },
    created () {
        Authorization.can({ action: 'create', model: 'project' }).then((can) => {
            this.canCreate = can
        })
    }
}
</script>

<style lang="scss">
.exemption-count {
    &:hover {
        text-decoration: none;

        .badge {
            background-color: var(--red-lighten-10);
        }
    }
}

.exemption-count__badge {
    padding: 5px 10px;
    font-size: 21px;
}
</style>
